// Quick Task 260909-gm3 — Admin: Add Hardware to Existing Site.
//
// Mounted inside SiteConfigView, below the existing config table. Fetches
// DB_Hardware[] from GET /api/admin/sites/{locationId}/hardware and lets an
// operator draft one or more new Dipstick name/description rows, debounce-checks
// each name against GET /api/admin/hardware/check-name (soft-fail — the POST
// transaction's in-DB collision re-check is the authoritative uniqueness gate,
// per T-GM3-02), then POSTs { Hardware: [...] } to the same hardware endpoint.
//
// SECURITY: Bearer JWT in Authorization header; the real authorization gate is
// server-side `[Authorize(Policy = "GlobalAdmin")]` on both endpoints.
// XSS: All cell content via React text-interpolation — no dangerouslySetInnerHTML.

import React, { useState, useCallback, useEffect } from 'react';
import { Table, Input, Button, Alert, FormText } from 'reactstrap';
import { apiBase } from './apiBase';

const DEBOUNCE_MS = 400; // matches StepC_Hardware.js D-04 knob

function getAuthToken(auth) {
    if (auth && typeof auth.getAccessToken === 'function') {
        try { return auth.getAccessToken(); } catch (e) { /* server will 401 */ }
    }
    return '';
}

export function SiteHardwareSection({ locationId, auth }) {
    const [existing, setExisting] = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);

    const [drafts, setDrafts]         = useState([]); // { name, description, collisionError }
    const [busy, setBusy]             = useState(false);
    const [saveError, setSaveError]   = useState(null);
    const [successIds, setSuccessIds] = useState(null); // AdminCreateSiteHardwareResult[] from last save

    const fetchExisting = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = getAuthToken(auth);
            const headers = token ? { Authorization: 'Bearer ' + token } : {};
            const res = await fetch(apiBase() + `/api/admin/sites/${locationId}/hardware`, { method: 'GET', headers });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error('HTTP ' + res.status + (text ? ': ' + text : ''));
            }
            const data = await res.json();
            setExisting(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e && e.message ? e.message : String(e));
            setExisting([]);
        } finally {
            setLoading(false);
        }
    }, [auth, locationId]);

    useEffect(() => { fetchExisting(); }, [fetchExisting]);

    // Debounced collision check — ported from StepC_Hardware.js. namesKey join-string
    // dependency hack: an array literal in the dep list would re-run the effect every
    // render even when names are unchanged.
    const namesKey = drafts.map((d) => d.name).join('|');

    useEffect(() => {
        const handles = drafts.map((row, idx) => {
            if (!row.name) return null; // empty — no probe
            const handle = setTimeout(async () => {
                try {
                    const token = getAuthToken(auth);
                    const res = await fetch(
                        `${apiBase()}/api/admin/hardware/check-name?name=${encodeURIComponent(row.name)}`,
                        { headers: token ? { Authorization: 'Bearer ' + token } : {} },
                    );
                    if (!res.ok) return; // soft-fail — POST is the authoritative uniqueness gate
                    const body = await res.json();
                    setDrafts((ds) => ds.map((d, i) => {
                        if (i !== idx) return d;
                        if (body && body.taken) {
                            return {
                                ...d,
                                collisionError:
                                    `${row.name} is already assigned to LocationId ${body.locationId} ` +
                                    `(${body.locationName} / ${body.orgName}). Pick a different name.`,
                            };
                        }
                        return { ...d, collisionError: null };
                    }));
                } catch {
                    // soft-fail on network error — see header comment
                }
            }, DEBOUNCE_MS);
            return handle;
        });
        return () => { handles.forEach((h) => h && clearTimeout(h)); };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- drafts referenced via namesKey
    }, [namesKey, auth]);

    const addRow = () => {
        setDrafts((ds) => [...ds, { name: '', description: '', collisionError: null }]);
        setSuccessIds(null);
    };

    const removeRow = (idx) => {
        setDrafts((ds) => ds.filter((_, i) => i !== idx));
    };

    const updateRow = (idx, patch) => {
        setDrafts((ds) => ds.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
    };

    const hasBlankName = drafts.some((d) => !d.name || !d.name.trim());
    const hasCollision = drafts.some((d) => !!d.collisionError);
    const saveDisabled = busy || drafts.length === 0 || hasBlankName || hasCollision;

    const onSaveClick = async () => {
        if (saveDisabled) return;
        setBusy(true);
        setSaveError(null);
        setSuccessIds(null);
        try {
            const token = getAuthToken(auth);
            const res = await fetch(apiBase() + `/api/admin/sites/${locationId}/hardware`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: 'Bearer ' + token } : {}),
                },
                body: JSON.stringify({
                    Hardware: drafts.map((d) => ({ Name: d.name, Description: d.description })),
                }),
            });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                // A 409 body carries the offending name and owning site — surface it verbatim
                // rather than a generic failure message.
                throw new Error(text || ('HTTP ' + res.status));
            }
            const body = await res.json();
            setDrafts([]);
            setSuccessIds(Array.isArray(body.hardware) ? body.hardware : (body.Hardware || []));
            await fetchExisting();
        } catch (e) {
            setSaveError(e && e.message ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="site-hardware-section">
            <h4 className="mt-4">Hardware</h4>
            {error && (
                <Alert color="danger" data-testid="site-hardware-load-error">
                    Failed to load hardware: {error}
                </Alert>
            )}
            <div data-testid="site-hardware-table">
                <Table size="sm" bordered hover responsive={false}>
                    <thead>
                        <tr>
                            <th>HardwareID</th>
                            <th>Name</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {existing.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="text-center text-muted">
                                    {loading ? 'Loading...' : 'No hardware.'}
                                </td>
                            </tr>
                        ) : (
                            existing.map((h) => (
                                <tr key={h.id} data-testid={`site-hardware-row-${h.id}`}>
                                    <td>{h.id}</td>
                                    <td>{h.name}</td>
                                    <td className="text-muted small">{h.description}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            {drafts.length > 0 && (
                <Table size="sm" bordered className="mt-2">
                    <thead>
                        <tr><th>Name</th><th>Description</th><th /></tr>
                    </thead>
                    <tbody>
                        {drafts.map((row, idx) => (
                            <tr key={idx}>
                                <td>
                                    <Input
                                        type="text"
                                        value={row.name}
                                        onChange={(e) => updateRow(idx, { name: e.target.value, collisionError: null })}
                                        invalid={!!row.collisionError}
                                        disabled={busy}
                                        data-testid={`site-hardware-name-${idx}`}
                                    />
                                    {row.collisionError && (
                                        <div className="text-danger small" data-testid={`site-hardware-collision-${idx}`}>
                                            {row.collisionError}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        value={row.description}
                                        onChange={(e) => updateRow(idx, { description: e.target.value })}
                                        disabled={busy}
                                        data-testid={`site-hardware-desc-${idx}`}
                                    />
                                </td>
                                <td>
                                    <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => removeRow(idx)}
                                        disabled={busy}
                                        data-testid={`site-hardware-remove-${idx}`}
                                    >
                                        Remove
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <div className="mt-2">
                <Button
                    color="secondary"
                    size="sm"
                    onClick={addRow}
                    disabled={busy}
                    data-testid="site-hardware-add-row"
                >
                    + Add Hardware
                </Button>
                {' '}
                <Button
                    color="primary"
                    size="sm"
                    onClick={onSaveClick}
                    disabled={saveDisabled}
                    data-testid="site-hardware-save"
                >
                    Save
                </Button>
            </div>
            <FormText>
                Names must be globally unique (collision-checked across all orgs).
            </FormText>

            {saveError && (
                <Alert color="danger" className="mt-2" data-testid="site-hardware-error">
                    {saveError}
                </Alert>
            )}
            {successIds && (
                <Alert color="success" className="mt-2" data-testid="site-hardware-success">
                    Added: {successIds.map((h) => `HardwareID ${h.id ?? h.Id} → ${h.name ?? h.Name}`).join(', ')}
                </Alert>
            )}
        </div>
    );
}

export default SiteHardwareSection;
